import { memo, useContext, useMemo, useRef } from "react";

import ArtifactCard from "./ArtifactCard";
import CompactToolBlock from "./CompactToolBlock";
import { Box, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { parsePayloadObject } from "src/reusable/Chat/ChatStreaming/services/contentTypeUtils";
import ChatContext from "../../context/ChatContext";
import {
  isInteractiveToolResponse,
  isToolResponseTextComplete,
  parseToolResponseText,
} from "./toolStatusUtils";

interface ToolCallProps {
  /** Raw tool call text including XML tags or JSON */
  text: string;
  /** Position of this part in the message's contentParts array */
  partIndex: number;
  /** ID of the parent message */
  messageId?: string | null;
  /** ID of the parent message in conversation thread */
  parentMessageId?: string | null;
  /** Whether a matching tool_response part exists after this tool_call */
  hasResponse?: boolean;
  /** Raw text of this call's own tool_response part, once it exists */
  responseText?: string;
  /** Whether the SSE connection is still active */
  isChatConnected?: boolean;
  /** Whether the message stream was stopped by the user */
  isChatMessageStreamStopped?: boolean;
}

/** Tool names that manage plan state - rendered compactly */
const PLAN_MANAGEMENT_TOOLS = new Set([
  "update_plan_step",
  "Update Plan Step",
  "add_plan_step",
  "Add Plan Step",
]);

/** Parsed tool call object structure */
export interface ToolCallObject {
  /** Name of the tool being called */
  name: string;
  /** Arguments passed to the tool */
  arguments: Record<string, any>;
  /** Human-readable status message from the model */
  status_message?: string;
}

/**
 * Validate and shape a parsed payload into a ToolCallObject.
 * Returns null if the payload lacks a valid tool name.
 */
function toToolCallObject(value: unknown): ToolCallObject | null {
  if (!value || typeof value !== "object") return null;
  const payload = value as Record<string, unknown>;
  if (typeof payload.name !== "string" || !payload.name.trim()) return null;
  return {
    name: payload.name,
    arguments:
      payload.arguments && typeof payload.arguments === "object"
        ? (payload.arguments as Record<string, any>)
        : {},
    status_message:
      typeof payload.status_message === "string"
        ? payload.status_message
        : undefined,
  };
}

/**
 * Extract first tool update message from text using <tool_update> tags.
 * During streaming, the backend sends early detection updates wrapped in
 * <tool_update> tags within the <tool_call> body. This extracts the latest
 * update and returns cleaned text for final JSON parsing.
 *
 * @returns Tuple of [toolUpdateJSON, cleanedText]
 */
const extractToolUpdate = (text: string): [ToolCallObject | null, string] => {
  let currentIndex = 0;
  let startIndex = 0;
  let endIndex = 0;
  let toolUpdate: ToolCallObject | null = null;
  let openTag = text.indexOf("<tool_update>", currentIndex);
  let closeTag = text.indexOf("</tool_update>", currentIndex);

  while (openTag !== -1 && closeTag !== -1 && closeTag > openTag) {
    const toolUpdateJSON = text
      .substring(openTag + "<tool_update>".length, closeTag)
      .trim();
    try {
      toolUpdate = JSON.parse(toolUpdateJSON);
    } catch (error) {
      console.warn("Error parsing tool update JSON:", error);
    }

    if (startIndex === 0) {
      startIndex = openTag;
    }
    endIndex = closeTag + "</tool_update>".length;
    currentIndex = endIndex;
    openTag = text.indexOf("<tool_update>", currentIndex);
    closeTag = text.indexOf("</tool_update>", currentIndex);
  }

  if (startIndex !== endIndex) {
    return [toolUpdate, text.slice(0, startIndex) + text.slice(endIndex)];
  }

  return [toolUpdate, text];
};

/**
 * ToolCall renders the header portion of a tool usage block.
 * Shows the tool name and model's status message. Pairs visually
 * with InlineToolOutput (rendered by ToolResponse) below it.
 */
/** A status message that just echoes the tool name (plus at most one word,
 *  e.g. "Explore Corpus: expected") carries no information. */
const isUninformativeStatusMessage = (
  message: string,
  toolName: string,
): boolean => {
  const norm = (s: string) =>
    s
      .toLowerCase()
      .replace(/[_\s]+/g, " ")
      .trim();
  const normalized = norm(message);
  const name = norm(toolName);
  if (!name || !normalized.startsWith(name)) return false;
  const rest = normalized.slice(name.length).replace(/^[\s:—–-]+/, "");
  return rest.split(/\s+/).filter(Boolean).length <= 1;
};

const ToolCall = memo(
  ({
    text,
    partIndex,
    messageId,
    hasResponse,
    responseText,
    isChatConnected = false,
    isChatMessageStreamStopped = false,
  }: ToolCallProps) => {
    // Stable identity of this run for the elapsed timer — survives the
    // component unmounting (e.g. Equipment AI's tab rail hiding the chat).
    const runKey = messageId != null ? `${messageId}-${partIndex}` : undefined;
    // Raw useContext (not useChatContext): ToolCall is also mounted outside
    // a ChatContext provider (e.g. the static alarm-analysis view).
    const chatContext = useContext(ChatContext);
    const compact = Boolean(chatContext?.compactToolCalls);
    const completedRef = useRef(false);
    const isComplete = useMemo(() => {
      // Once complete, never revert (prevents timer restart on next turn)
      if (completedRef.current) return true;
      // Stream was stopped by user — treat all tools as complete immediately
      if (isChatMessageStreamStopped) {
        completedRef.current = true;
        return true;
      }
      const normalizedText = text.trimStart();
      // Old protocol: check for closing XML tag
      if (normalizedText.includes("</tool_call>")) {
        completedRef.current = true;
        return true;
      }
      // New protocol (native tool calling): JSON arrives as a complete blob,
      // but the tool is still "loading" until the matching tool_response part
      // appears. hasResponse tells us whether the response has been received.
      // If disconnected, treat as complete — prevents stuck timers when
      // content_type is lost on history reload.
      if (!normalizedText.startsWith("<tool_call>")) {
        const done = hasResponse ?? !isChatConnected;
        if (done) completedRef.current = true;
        return done;
      }
      // Still streaming XML — not complete yet
      const done = !isChatConnected;
      if (done) completedRef.current = true;
      return done;
    }, [text, hasResponse, isChatConnected, isChatMessageStreamStopped]);

    const toolCall = useMemo<ToolCallObject | null>(() => {
      const trimmed = text.trimStart();

      // Native protocol: text is raw JSON (no XML wrapper).
      // During streaming, early + final JSON objects may be concatenated
      // (e.g., '{"name":"tool",...}{"name":"tool","arguments":{...}}').
      // Parse the LAST complete JSON object to get the most up-to-date data.
      if (!trimmed.startsWith("<tool_call>")) {
        return toToolCallObject(parsePayloadObject(trimmed));
      }

      // Legacy XML protocol: extract early <tool_update> or parse completed call
      const [toolUpdate, cleanedText] = extractToolUpdate(text);
      if (!isComplete) return toolUpdate;

      const rawToolCall = cleanedText
        .replace(/^<tool_call>\s*/, "")
        .replace(/\s*<\/tool_call>$/, "");

      const parsed = toToolCallObject(parsePayloadObject(rawToolCall));
      if (!parsed) {
        console.warn("Error parsing tool call payload:", rawToolCall);
      }
      return parsed;
    }, [text, isComplete]);

    // Compact mode: this call's own response payload, for the ⎿ summary line
    // and the expanded raw-output panel.
    const responseContent = useMemo(() => {
      if (
        !compact ||
        !responseText ||
        !isToolResponseTextComplete(responseText, isChatConnected)
      ) {
        return undefined;
      }
      return parseToolResponseText(responseText);
    }, [compact, responseText, isChatConnected]);

    // Interactive results (prompts, downloads, previews) render their own UI
    // via ToolResponse below — don't duplicate them in the compact block.
    const hideResult = useMemo(
      () => isInteractiveToolResponse(responseContent),
      [responseContent],
    );

    if (!toolCall) return null;

    const statusMessageFromArgs =
      typeof toolCall.arguments?.status_message === "string"
        ? toolCall.arguments.status_message
        : undefined;
    const rawStatusMessage = toolCall.status_message || statusMessageFromArgs;
    // Models sometimes echo the tool name as the status message ("Explore
    // Corpus: expected") — drop it so the name + args preview renders instead.
    const displayStatusMessage =
      rawStatusMessage &&
      isUninformativeStatusMessage(rawStatusMessage, toolCall.name || "")
        ? undefined
        : rawStatusMessage;

    // Compact rendering for plan management tools
    if (PLAN_MANAGEMENT_TOOLS.has(toolCall.name)) {
      const stepStatus = toolCall.arguments?.status;
      const stepId = toolCall.arguments?.step_id;
      const label =
        toolCall.name.includes("add") || toolCall.name.includes("Add")
          ? `Adding step: ${toolCall.arguments?.title || stepId || ""}`
          : `${stepId || "step"}: ${stepStatus || "updating"}`;
      return (
        <Typography
          variant="caption"
          sx={{
            color: grey[500],
            fontSize: "0.7rem",
            pl: 0.5,
            py: 0.25,
            display: "block",
          }}
        >
          {label}
        </Typography>
      );
    }

    if (compact) {
      return (
        <CompactToolBlock
          toolName={toolCall.name}
          isLoading={!isComplete}
          statusMessage={displayStatusMessage}
          toolArguments={toolCall.arguments}
          responseContent={responseContent}
          hideResult={hideResult}
          runKey={runKey}
        />
      );
    }

    return (
      <Box sx={{ mb: 0 }}>
        <ArtifactCard
          toolName={toolCall.name}
          isLoading={!isComplete}
          isConnected={isComplete}
          statusMessage={displayStatusMessage}
          arguments={toolCall.arguments}
          runKey={runKey}
        />
      </Box>
    );
  },
);

export default ToolCall;
